import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { AuthModal } from "../components/AuthModal";
import { MapView } from "../components/MapView";
import { useAuth } from "../context/AuthContext";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Search as SearchIcon, MapPin, Gauge, Wifi, BadgeCheck, Star, X, Signal, Lock, Copy, Map as MapIcon, LayoutGrid, MessageSquare, ImagePlus, Bookmark } from "lucide-react";

export default function Search() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [city, setCity] = useState("");
  const [minSpeed, setMinSpeed] = useState(0);
  const [wifi6, setWifi6] = useState(false);
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(searchParams.get("view") === "map" ? "map" : "grid");
  const [selected, setSelected] = useState(null);
  const [creds, setCreds] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [postingReview, setPostingReview] = useState(false);
  const [myPhoto, setMyPhoto] = useState(null);
  const photoRef = useRef(null);
  const [saved, setSaved] = useState([]);

  const loadSaved = async () => {
    if (!user) { setSaved([]); return; }
    try { const { data } = await api.get("/saved-searches"); setSaved(data); } catch { /* noop */ }
  };
  useEffect(() => { loadSaved(); /* eslint-disable-next-line */ }, [user]);

  const saveSearch = async () => {
    if (!user) { setAuthOpen(true); return; }
    const label = city ? `${city}` : "My area";
    try { await api.post("/saved-searches", { label, city: city || null }); toast.success("Search saved — we'll email you about new hotspots."); loadSaved(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const removeSaved = async (id) => {
    try { await api.delete(`/saved-searches/${id}`); loadSaved(); } catch { /* noop */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (city) params.city = city;
      if (minSpeed) params.min_speed = minSpeed;
      if (wifi6) params.wifi6_only = true;
      const { data } = await api.get("/hotspots", { params });
      setSpots(data);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openSpot = useCallback(async (spot) => {
    setSelected(spot); setCreds(null); setMyRating(0); setMyComment("");
    try { const { data } = await api.get(`/hotspots/${spot.hotspot_id}/reviews`); setReviews(data); }
    catch { setReviews([]); }
  }, []);

  const connect = async (spot) => {
    if (!user) { setAuthOpen(true); return; }
    if (user.verification_status !== "verified") { toast.error("Verify your identity first."); navigate("/verify"); return; }
    if (user.subscription_status !== "active") { toast.error("An active membership is required."); navigate("/plans"); return; }
    setRequesting(true);
    try {
      const { data } = await api.post(`/hotspots/${spot.hotspot_id}/request`);
      setCreds(data);
      toast.success("Connected! Credentials unlocked.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(formatApiErrorDetail(d) || err.message);
      if (err.response?.status === 403) navigate("/verify");
      if (err.response?.status === 402) navigate("/plans");
    } finally { setRequesting(false); }
  };

  const submitReview = async () => {
    if (!myRating) { toast.error("Pick a star rating"); return; }
    setPostingReview(true);
    try {
      const fd = new FormData();
      fd.append("rating", myRating);
      fd.append("comment", myComment);
      if (myPhoto) fd.append("photo", myPhoto);
      const { data } = await api.post(`/hotspots/${selected.hotspot_id}/reviews`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thanks for your review!");
      setSelected({ ...selected, rating: data.rating, review_count: data.review_count });
      const res = await api.get(`/hotspots/${selected.hotspot_id}/reviews`);
      setReviews(res.data); setMyRating(0); setMyComment(""); setMyPhoto(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setPostingReview(false); }
  };

  const switchView = (v) => { setView(v); setSearchParams(v === "map" ? { view: "map" } : {}); };

  const searchArea = async (bounds) => {
    try {
      const { data } = await api.get("/hotspots", { params: bounds });
      setSpots(data);
      toast.success(`${data.length} hotspot${data.length === 1 ? "" : "s"} in this area`);
    } catch { toast.error("Could not search this area"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="min-h-screen bg-background">
      <Navbar onRequireAuth={() => setAuthOpen(true)} />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Find nearby Wi-Fi</h1>
            <p className="text-muted-foreground">Verified hotspots shared by hosts near you.</p>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-border p-1 self-start">
            <button onClick={() => switchView("grid")} data-testid="view-grid" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${view === "grid" ? "bg-foreground text-background" : "text-muted-foreground"}`}><LayoutGrid size={14} /> Grid</button>
            <button onClick={() => switchView("map")} data-testid="view-map" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${view === "map" ? "bg-foreground text-background" : "text-muted-foreground"}`}><MapIcon size={14} /> Map</button>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4 mb-8 grid gap-3 md:grid-cols-[1fr_auto_auto_auto]">
          <div className="relative">
            <SearchIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={city} onChange={(e) => setCity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="City (e.g. Memphis, Brooklyn)" data-testid="search-city-input"
              className="w-full h-11 pl-11 pr-4 rounded-xl bg-background border border-input text-sm outline-none focus:border-ring" />
          </div>
          <div className="flex items-center gap-2 px-4 h-11 rounded-xl bg-background border border-input">
            <Gauge size={15} className="text-muted-foreground" />
            <select value={minSpeed} onChange={(e) => setMinSpeed(Number(e.target.value))} data-testid="search-speed-select" className="bg-transparent text-sm outline-none">
              <option value={0}>Any speed</option><option value={100}>100+ Mbps</option><option value={300}>300+ Mbps</option><option value={500}>500+ Mbps</option>
            </select>
          </div>
          <button onClick={() => setWifi6(!wifi6)} data-testid="search-wifi6-toggle"
            className={`flex items-center gap-2 px-4 h-11 rounded-xl border text-sm transition-colors ${wifi6 ? "bg-foreground text-background border-foreground" : "bg-background border-input text-muted-foreground"}`}>
            <Wifi size={15} /> Wi-Fi 6
          </button>
          <button onClick={load} data-testid="search-submit" className="h-11 px-6 rounded-xl bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity">Search</button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="saved-searches-bar">
          <button onClick={saveSearch} data-testid="save-search-btn" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-border hover:bg-accent transition-colors">
            <Bookmark size={13} /> Save this search
          </button>
          {saved.map((s) => (
            <span key={s.saved_search_id} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-accent" data-testid={`saved-${s.saved_search_id}`}>
              {s.label}
              <button onClick={() => removeSaved(s.saved_search_id)} className="text-muted-foreground hover:text-foreground" data-testid={`remove-saved-${s.saved_search_id}`}><X size={12} /></button>
            </span>
          ))}
        </div>

        {loading ? (
          <p className="text-muted-foreground font-mono text-sm py-20 text-center">Scanning network…</p>
        ) : spots.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-2xl">
            <Signal size={28} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No hotspots found. Try a different city or be the first to host one.</p>
          </div>
        ) : view === "map" ? (
          <MapView spots={spots} onSelect={openSpot} onSearchArea={searchArea} />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="hotspot-grid">
            {spots.map((s) => (
              <div key={s.hotspot_id} data-testid={`hotspot-card-${s.hotspot_id}`}
                className="rounded-2xl border border-border bg-card p-6 hover:border-zinc-400 dark:hover:border-zinc-600 transition-all flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-display text-lg font-medium">{s.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={13} /> {s.city}</p>
                  </div>
                  {s.wifi6 && <span className="font-mono text-[10px] uppercase px-2 py-1 rounded-full bg-accent">Wi-Fi 6</span>}
                </div>
                <div className="flex items-center gap-4 text-sm mb-4">
                  <span className="font-mono font-bold">{s.speed_mbps} <span className="text-muted-foreground font-normal text-xs">Mbps</span></span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Star size={13} className={s.review_count ? "fill-amber-400 text-amber-400" : "text-muted-foreground"} /> {s.review_count ? `${s.rating} (${s.review_count})` : "New"}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-5 text-xs">
                  <span className="flex items-center gap-1 text-muted-foreground">by {s.host_name || "Host"}</span>
                  {s.host_verified && <span className="flex items-center gap-1 text-emerald-500"><BadgeCheck size={13} /> Verified</span>}
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <span className="text-sm text-muted-foreground"><span className="font-bold text-foreground">${s.monthly_price}</span>/mo</span>
                  <button onClick={() => openSpot(s)} data-testid={`view-hotspot-${s.hotspot_id}`} className="text-sm px-4 py-2 rounded-full border border-border hover:bg-accent transition-colors">View</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {selected && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="hotspot-detail-modal">
          <div className="relative w-full max-w-lg my-8 rounded-2xl border border-border bg-card p-8 rise">
            <button onClick={() => { setSelected(null); setCreds(null); }} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"><X size={20} /></button>
            <h2 className="font-display text-2xl font-bold">{selected.name}</h2>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={14} /> {selected.city} {selected.address && `· ${selected.address}`}</p>
            <div className="flex items-center gap-1 mt-2 text-sm">
              <Star size={14} className={selected.review_count ? "fill-amber-400 text-amber-400" : "text-muted-foreground"} />
              <span className="font-medium">{selected.review_count ? selected.rating : "New"}</span>
              <span className="text-muted-foreground">{selected.review_count ? `· ${selected.review_count} review${selected.review_count > 1 ? "s" : ""}` : ""}</span>
            </div>

            <div className="grid grid-cols-3 gap-3 my-6">
              <div className="rounded-xl border border-border p-3 text-center"><div className="font-mono text-xl font-bold">{selected.speed_mbps}</div><div className="text-xs text-muted-foreground">Mbps</div></div>
              <div className="rounded-xl border border-border p-3 text-center"><div className="font-mono text-xl font-bold">{selected.max_guests}</div><div className="text-xs text-muted-foreground">max guests</div></div>
              <div className="rounded-xl border border-border p-3 text-center"><div className="font-mono text-xl font-bold">{selected.bandwidth_limit_gb}</div><div className="text-xs text-muted-foreground">GB cap</div></div>
            </div>

            {selected.description && <p className="text-sm text-muted-foreground mb-6">{selected.description}</p>}

            {creds ? (
              <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-5 mb-6" data-testid="wifi-credentials">
                <p className="font-mono text-xs uppercase tracking-wider text-emerald-500 flex items-center gap-1 mb-3"><Lock size={13} /> Encrypted credentials unlocked</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Network</span><button onClick={() => copy(creds.ssid)} className="font-mono text-sm flex items-center gap-2">{creds.ssid} <Copy size={13} /></button></div>
                  <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Password</span><button onClick={() => copy(creds.wifi_password)} className="font-mono text-sm flex items-center gap-2">{creds.wifi_password} <Copy size={13} /></button></div>
                </div>
              </div>
            ) : (
              <button onClick={() => connect(selected)} disabled={requesting} data-testid="connect-hotspot-btn"
                className="w-full h-12 rounded-xl bg-foreground text-background font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 mb-6">
                {requesting ? "Connecting…" : `Connect · $${selected.monthly_price}/mo`}
              </button>
            )}

            {/* Reviews */}
            <div className="border-t border-border pt-5">
              <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-4"><MessageSquare size={13} /> Reviews</p>

              {creds && (
                <div className="rounded-xl border border-border p-4 mb-4" data-testid="review-form">
                  <p className="text-sm font-medium mb-2">Rate your experience</p>
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} onClick={() => setMyRating(n)} data-testid={`star-${n}`}>
                        <Star size={22} className={n <= myRating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"} />
                      </button>
                    ))}
                  </div>
                  <textarea value={myComment} onChange={(e) => setMyComment(e.target.value)} placeholder="Share details about the speed and reliability…" rows={2} data-testid="review-comment"
                    className="w-full px-3 py-2 rounded-lg bg-background border border-input text-sm outline-none focus:border-ring resize-none mb-2" />
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" data-testid="review-photo-input" onChange={(e) => setMyPhoto(e.target.files[0])} />
                  <button type="button" onClick={() => photoRef.current.click()} data-testid="review-add-photo" className="mb-2 flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
                    <ImagePlus size={14} /> {myPhoto ? myPhoto.name : "Add a photo (speed test / connection)"}
                  </button>
                  <div>
                  <button onClick={submitReview} disabled={postingReview} data-testid="submit-review"
                    className="h-10 px-5 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">{postingReview ? "Posting…" : "Post review"}</button>
                  </div>
                </div>
              )}

              {reviews.length === 0 ? (
                <p className="text-sm text-muted-foreground">No reviews yet{creds ? " — be the first!" : "."}</p>
              ) : (
                <div className="space-y-3" data-testid="reviews-list">
                  {reviews.map((r, i) => (
                    <div key={i} className="rounded-xl border border-border p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{r.guest_name || "Member"}</span>
                        <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"} />)}</span>
                      </div>
                      {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
                      {r.photo_path && (
                        <img src={`${API}/review-photos/${r.photo_path}`} alt="review" data-testid="review-photo" className="mt-2 rounded-lg border border-border max-h-40 object-cover" />
                      )}
                      {r.host_reply && (
                        <div className="mt-2 pl-3 border-l-2 border-emerald-500/50">
                          <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Host reply:</span> {r.host_reply}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode="signin" />
    </div>
  );
}
